import { CustomerLedgerUpdateTypeEnum, toBigWithDefault, type CustomerBalance } from '@talos/kyoko';
import { useMemo } from 'react';

/**
 * Calculate the withdrawable amount for a customer balance
 * Formula: Amount - PendingWithdrawal - OutstandingSell
 * @param customerBalance - Customer's balance data
 * @returns Withdrawable amount as Big number, or Big(0) if invalid
 */
function calculateWithdrawableAmount(customerBalance?: CustomerBalance) {
  if (!customerBalance) {
    return toBigWithDefault(0, 0);
  }

  const amount = toBigWithDefault(customerBalance.Amount, 0);
  const pendingWithdrawal = toBigWithDefault(customerBalance.PendingWithdrawal, 0);
  const outstandingSell = toBigWithDefault(customerBalance.OutstandingSell, 0);

  const result = amount.minus(pendingWithdrawal).minus(outstandingSell);

  // Ensure the result is not negative
  return result.lt(0) ? toBigWithDefault(0, 0) : result;
}

/**
 * Calculate the withdrawable amount for a customer balance as a number
 * Formula: Amount - PendingWithdrawal - OutstandingSell
 * @param customerBalance - Customer's balance data
 * @returns Withdrawable amount as number, or 0 if invalid
 */
export function getWithdrawableAmount(customerBalance?: CustomerBalance): number {
  return calculateWithdrawableAmount(customerBalance).toNumber();
}

export interface AmountValidationOptions {
  amount?: string;
  transferType?: CustomerLedgerUpdateTypeEnum;
  customerBalance?: CustomerBalance;
  skipValidationForForceWithdraw?: boolean;
}

/**
 * Custom hook for validating transaction amounts
 * @param options - Validation parameters
 * @returns Error message string if validation fails, undefined if valid
 */
export function useAmountValidation({
  amount,
  transferType,
  customerBalance,
  skipValidationForForceWithdraw = false,
}: AmountValidationOptions): string | undefined {
  return useMemo(() => {
    return validateAmount(amount, transferType, customerBalance, skipValidationForForceWithdraw);
  }, [amount, transferType, customerBalance, skipValidationForForceWithdraw]);
}

/**
 * Validates transaction amount against available balance
 * @param amount - Amount to validate
 * @param transferType - Type of transfer (Deposit/Withdraw/Set)
 * @param customerBalance - Customer's current balance
 * @param skipValidationForForceWithdraw - Whether to skip validation for force withdrawals
 * @returns Error message string if validation fails, undefined if valid
 */
export function validateAmount(
  amount?: string,
  transferType?: CustomerLedgerUpdateTypeEnum,
  customerBalance?: CustomerBalance,
  skipValidationForForceWithdraw = false
): string | undefined {
  // Skip validation if basic requirements not met
  if (!amount || transferType !== CustomerLedgerUpdateTypeEnum.Withdraw || !customerBalance) {
    return undefined;
  }

  // Skip validation for force withdrawals if specified
  if (skipValidationForForceWithdraw) {
    return undefined;
  }

  const bigAmount = toBigWithDefault(amount, 0);
  const withdrawableAmount = calculateWithdrawableAmount(customerBalance);

  // Check if amount is negative or zero
  if (bigAmount.lte(0)) {
    return 'Amount must be greater than zero';
  }

  // Check if amount exceeds withdrawable balance
  if (bigAmount.gt(withdrawableAmount)) {
    return 'Amount is greater than withdrawable customer balance';
  }

  return undefined;
}
